import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Navbar, Footer } from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import { Search, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Comprehensive brand list for search requests
const COMMON_BRANDS = [
  'Alfa Romeo', 'Aston Martin', 'Audi', 'Bentley', 'BMW', 'Bugatti',
  'Cadillac', 'Chevrolet', 'Chrysler', 'Citroën', 'Cupra', 'Dacia',
  'Dodge', 'DS Automobiles', 'Ferrari', 'Fiat', 'Ford', 'Genesis',
  'Honda', 'Hyundai', 'Infiniti', 'Isuzu', 'Jaguar', 'Jeep', 'Kia',
  'Lamborghini', 'Lancia', 'Land Rover', 'Lexus', 'Lotus', 'Maserati',
  'Mazda', 'McLaren', 'Mercedes-Benz', 'MG', 'MINI', 'Mitsubishi',
  'Nissan', 'Opel', 'Peugeot', 'Polestar', 'Porsche', 'Renault',
  'Rolls-Royce', 'Seat', 'Škoda', 'Smart', 'SsangYong', 'Subaru',
  'Suzuki', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo'
];

const VEHICLE_TYPES = [
  { value: 'Limousine', de: 'Limousine', en: 'Sedan' },
  { value: 'Kombi', de: 'Kombi', en: 'Estate/Wagon' },
  { value: 'SUV', de: 'SUV', en: 'SUV' },
  { value: 'Coupé', de: 'Coupé', en: 'Coupé' },
  { value: 'Cabrio', de: 'Cabrio', en: 'Convertible' },
  { value: 'Kleinwagen', de: 'Kleinwagen', en: 'Compact' },
  { value: 'Van', de: 'Van', en: 'Van' },
  { value: 'Pickup', de: 'Pickup', en: 'Pickup' },
  { value: 'Sonstiges', de: 'Sonstiges', en: 'Other' },
];

const FUEL_TYPES = [
  { value: 'Benzin', de: 'Benzin', en: 'Petrol' },
  { value: 'Diesel', de: 'Diesel', en: 'Diesel' },
  { value: 'Hybrid', de: 'Hybrid', en: 'Hybrid' },
  { value: 'Plugin-Hybrid', de: 'Plug-in Hybrid', en: 'Plug-in Hybrid' },
  { value: 'Elektro', de: 'Elektro', en: 'Electric' },
  { value: 'Sonstiges', de: 'Sonstiges', en: 'Other' },
];

const TRANSMISSIONS = [
  { value: 'Schaltgetriebe', de: 'Manuell', en: 'Manual' },
  { value: 'Automatik', de: 'Automatik', en: 'Automatic' },
];

const DRIVETRAINS = [
  { value: 'Frontantrieb', de: 'Front', en: 'FWD' },
  { value: 'Heckantrieb', de: 'Heck', en: 'RWD' },
  { value: 'Allrad', de: 'Allrad', en: 'AWD' },
];

const COLORS = [
  { value: 'Schwarz', de: 'Schwarz', en: 'Black' },
  { value: 'Weiß', de: 'Weiß', en: 'White' },
  { value: 'Silber', de: 'Silber', en: 'Silver' },
  { value: 'Grau', de: 'Grau', en: 'Grey' },
  { value: 'Blau', de: 'Blau', en: 'Blue' },
  { value: 'Rot', de: 'Rot', en: 'Red' },
  { value: 'Grün', de: 'Grün', en: 'Green' },
  { value: 'Braun', de: 'Braun', en: 'Brown' },
  { value: 'Beige', de: 'Beige', en: 'Beige' },
  { value: 'Gold', de: 'Gold', en: 'Gold' },
  { value: 'Orange', de: 'Orange', en: 'Orange' },
  { value: 'Gelb', de: 'Gelb', en: 'Yellow' },
];

const EQUIPMENT_OPTIONS = [
  { value: 'Leder', de: 'Leder', en: 'Leather' },
  { value: 'Sitzheizung', de: 'Sitzheizung', en: 'Heated seats' },
  { value: 'Sitzbelüftung', de: 'Sitzbelüftung', en: 'Ventilated seats' },
  { value: 'Lenkradheizung', de: 'Lenkradheizung', en: 'Heated steering' },
  { value: 'Panoramadach', de: 'Panorama-/Schiebedach', en: 'Panoramic/sunroof' },
  { value: 'Navigation', de: 'Navigation', en: 'Navigation' },
  { value: 'Apple CarPlay', de: 'Apple CarPlay', en: 'Apple CarPlay' },
  { value: 'Android Auto', de: 'Android Auto', en: 'Android Auto' },
  { value: 'Rückfahrkamera', de: 'Rückfahrkamera', en: 'Backup camera' },
  { value: '360-Kamera', de: '360° Kamera', en: '360° Camera' },
  { value: 'Einparkhilfe', de: 'Einparkhilfe', en: 'Parking assist' },
  { value: 'LED-Scheinwerfer', de: 'LED-/Matrix-Scheinwerfer', en: 'LED/Matrix headlights' },
  { value: 'Adaptiver Tempomat', de: 'Adaptiver Tempomat', en: 'Adaptive cruise' },
  { value: 'Anhängerkupplung', de: 'Anhängerkupplung', en: 'Tow bar' },
  { value: 'Head-up-Display', de: 'Head-up-Display', en: 'Head-up display' },
  { value: 'Keyless Go', de: 'Keyless Go', en: 'Keyless entry' },
  { value: 'Sonstiges', de: 'Sonstiges', en: 'Other' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 40 }, (_, i) => currentYear - i);

const inputCls = 'input-premium w-full border border-border rounded bg-background text-foreground';

export default function WeFindPage() {
  const { t, language } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    brand: '',
    model: '',
    year_from: '',
    year_to: '',
    price_from: '',
    price_to: '',
    max_mileage: '',
    vehicle_type: '',
    fuel_type: '',
    transmission: '',
    colors: [],
    min_horsepower: '',
    drivetrain: '',
    equipment: [],
    additional_wishes: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    preferred_contact: '',
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const toggleArrayItem = (key, value) => {
    setForm(prev => {
      const arr = prev[key] || [];
      return {
        ...prev,
        [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
      };
    });
  };

  const label = (item) => language === 'de' ? item.de : item.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error(language === 'de' ? 'Bitte Pflichtfelder ausfüllen.' : 'Please fill in required fields.');
      return;
    }
    setSubmitting(true);
    try {
      // Clean numeric fields
      const payload = { ...form, language };
      ['year_from', 'year_to', 'max_mileage', 'min_horsepower'].forEach(k => {
        payload[k] = payload[k] ? parseInt(payload[k], 10) : null;
      });
      ['price_from', 'price_to'].forEach(k => {
        payload[k] = payload[k] ? parseFloat(payload[k]) : null;
      });
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });

      const res = await fetch(`${API_URL}/api/car-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(`${t('we_find_error')} (${err.message})`);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <section className="pt-40 pb-32">
          <div className="max-w-2xl mx-auto px-6 text-center" data-testid="we-find-success">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="text-emerald-600" size={40} />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-light mb-4">
              {t('we_find_success_title')}
            </h1>
            <p className="text-muted-foreground text-lg mb-8">
              {t('we_find_success_message')}
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white rounded hover:bg-neutral-800 transition-colors"
            >
              {language === 'de' ? 'Zurück zur Startseite' : 'Back to home'}
            </a>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{t('we_find_title')} | Tim Automobile</title>
        <meta name="description" content={t('we_find_intro_text')} />
      </Helmet>

      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-8 md:pt-40 md:pb-12">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
              <Search className="text-gold" size={20} />
            </div>
            <p className="overline">{language === 'de' ? 'Fahrzeugsuche' : 'Vehicle Search'}</p>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light mb-4" data-testid="we-find-title">
            {t('we_find_intro_headline')}
          </h1>
          <p className="text-muted-foreground text-lg">
            {t('we_find_intro_text')}
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="we-find-form">

            {/* Vehicle preferences */}
            <div className="bg-card border border-border p-6 md:p-8 rounded-lg">
              <h2 className="font-heading text-2xl mb-6 pb-3 border-b border-border">
                {t('we_find_section_vehicle')}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2">{t('we_find_brand')}</label>
                  <select
                    value={form.brand}
                    onChange={(e) => set('brand', e.target.value)}
                    className={inputCls}
                    data-testid="we-find-brand"
                  >
                    <option value="">{t('we_find_select_optional')}</option>
                    {COMMON_BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_model')}</label>
                  <input
                    type="text"
                    value={form.model}
                    onChange={(e) => set('model', e.target.value)}
                    placeholder={language === 'de' ? 'z.B. M3, 911, C-Klasse …' : 'e.g. M3, 911, C-Class …'}
                    className={inputCls}
                    data-testid="we-find-model"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_year_from')}</label>
                  <select value={form.year_from} onChange={(e) => set('year_from', e.target.value)} className={inputCls} data-testid="we-find-year-from">
                    <option value="">{t('we_find_select_optional')}</option>
                    {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_year_to')}</label>
                  <select value={form.year_to} onChange={(e) => set('year_to', e.target.value)} className={inputCls} data-testid="we-find-year-to">
                    <option value="">{t('we_find_select_optional')}</option>
                    {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_price_from')}</label>
                  <input type="number" min="0" step="500" value={form.price_from} onChange={(e) => set('price_from', e.target.value)} placeholder="10000" className={inputCls} data-testid="we-find-price-from" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_price_to')}</label>
                  <input type="number" min="0" step="500" value={form.price_to} onChange={(e) => set('price_to', e.target.value)} placeholder="80000" className={inputCls} data-testid="we-find-price-to" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_max_mileage')}</label>
                  <input type="number" min="0" step="1000" value={form.max_mileage} onChange={(e) => set('max_mileage', e.target.value)} placeholder="100000" className={inputCls} data-testid="we-find-max-mileage" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_min_hp')}</label>
                  <input type="number" min="0" step="10" value={form.min_horsepower} onChange={(e) => set('min_horsepower', e.target.value)} placeholder="150" className={inputCls} data-testid="we-find-min-hp" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_vehicle_type')}</label>
                  <select value={form.vehicle_type} onChange={(e) => set('vehicle_type', e.target.value)} className={inputCls} data-testid="we-find-vehicle-type">
                    <option value="">{t('we_find_select_optional')}</option>
                    {VEHICLE_TYPES.map(v => <option key={v.value} value={v.value}>{label(v)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_fuel')}</label>
                  <select value={form.fuel_type} onChange={(e) => set('fuel_type', e.target.value)} className={inputCls} data-testid="we-find-fuel">
                    <option value="">{t('we_find_select_optional')}</option>
                    {FUEL_TYPES.map(v => <option key={v.value} value={v.value}>{label(v)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_transmission')}</label>
                  <select value={form.transmission} onChange={(e) => set('transmission', e.target.value)} className={inputCls} data-testid="we-find-transmission">
                    <option value="">{t('we_find_select_optional')}</option>
                    {TRANSMISSIONS.map(v => <option key={v.value} value={v.value}>{label(v)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_drivetrain')}</label>
                  <select value={form.drivetrain} onChange={(e) => set('drivetrain', e.target.value)} className={inputCls} data-testid="we-find-drivetrain">
                    <option value="">{t('we_find_select_optional')}</option>
                    {DRIVETRAINS.map(v => <option key={v.value} value={v.value}>{label(v)}</option>)}
                  </select>
                </div>
              </div>

              {/* Colors */}
              <div className="mt-6">
                <label className="overline block mb-3">{t('we_find_colors')}</label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map(c => {
                    const active = form.colors.includes(c.value);
                    return (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => toggleArrayItem('colors', c.value)}
                        className={`px-4 py-2 rounded-full text-sm transition-colors border ${active ? 'bg-foreground text-background border-foreground' : 'bg-background text-foreground border-border hover:border-foreground'}`}
                        data-testid={`we-find-color-${c.value}`}
                      >
                        {label(c)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Equipment */}
            <div className="bg-card border border-border p-6 md:p-8 rounded-lg">
              <h2 className="font-heading text-2xl mb-6 pb-3 border-b border-border">
                {t('we_find_section_equipment')}
              </h2>
              <div className="flex flex-wrap gap-2">
                {EQUIPMENT_OPTIONS.map(eq => {
                  const active = form.equipment.includes(eq.value);
                  return (
                    <button
                      key={eq.value}
                      type="button"
                      onClick={() => toggleArrayItem('equipment', eq.value)}
                      className={`px-4 py-2 rounded-full text-sm transition-colors border ${active ? 'bg-gold text-white border-gold' : 'bg-background text-foreground border-border hover:border-gold'}`}
                      data-testid={`we-find-eq-${eq.value}`}
                    >
                      {label(eq)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Additional wishes */}
            <div className="bg-card border border-border p-6 md:p-8 rounded-lg">
              <h2 className="font-heading text-2xl mb-6 pb-3 border-b border-border">
                {t('we_find_section_wishes')}
              </h2>
              <textarea
                value={form.additional_wishes}
                onChange={(e) => set('additional_wishes', e.target.value)}
                placeholder={t('we_find_wishes_placeholder')}
                rows={5}
                className={`${inputCls} resize-y`}
                data-testid="we-find-wishes"
              />
            </div>

            {/* Contact */}
            <div className="bg-card border border-border p-6 md:p-8 rounded-lg">
              <h2 className="font-heading text-2xl mb-2 pb-3 border-b border-border">
                {t('we_find_section_contact')}
              </h2>
              <p className="text-xs text-muted-foreground mb-6">{t('we_find_required_note')}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2">{t('we_find_first_name')} *</label>
                  <input type="text" required value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className={inputCls} data-testid="we-find-first-name" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_last_name')} *</label>
                  <input type="text" required value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className={inputCls} data-testid="we-find-last-name" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_email')} *</label>
                  <input type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} data-testid="we-find-email" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('we_find_phone')}</label>
                  <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputCls} data-testid="we-find-phone" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-2">{t('we_find_preferred_contact')}</label>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { value: 'email', label: t('we_find_preferred_email') },
                      { value: 'phone', label: t('we_find_preferred_phone') },
                      { value: 'any', label: t('we_find_preferred_any') },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => set('preferred_contact', form.preferred_contact === opt.value ? '' : opt.value)}
                        className={`px-4 py-2 rounded-full text-sm transition-colors border ${form.preferred_contact === opt.value ? 'bg-foreground text-background border-foreground' : 'bg-background text-foreground border-border hover:border-foreground'}`}
                        data-testid={`we-find-preferred-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-white rounded font-medium text-lg hover:bg-gold-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="we-find-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    {t('we_find_submitting')}
                  </>
                ) : (
                  <>
                    {t('we_find_submit')}
                    <Search size={18} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
